import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  PenLine,
  Share2,
  Heart,
  Wallet,
  CheckCircle2,
  Shield,
  BadgeCheck,
  Eye,
  Users,
  Globe,
  Clock,
  ArrowRight,
  Play,
  Sparkles,
  Target,
  Lock,
  CreditCard,
  Star,
  Quote,
} from 'lucide-react';
import apiClient from '../services/api';

const HowItWorksPage = () => {
  const [successStories, setSuccessStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(true);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await apiClient.get('/success-stories/approved?limit=6');
        setSuccessStories(response.data.stories || []);
      } catch (err) {
        console.log('Could not fetch success stories');
      } finally {
        setStoriesLoading(false);
      }
    };
    fetchStories();
  }, []);

  const steps = [
    {
      number: '01',
      icon: PenLine,
      title: 'Create Your Campaign',
      description: 'Set up your campaign in minutes with our easy-to-use builder. Add your story, photos, and set your fundraising goal.',
      features: [
        'Choose from 8+ categories',
        'AI-powered story suggestions',
        'Set milestones for transparency',
        'Upload images and videos',
      ],
      image: 'https://images.unsplash.com/photo-1504805572947-34fad45aed93?q=80&w=1000',
    },
    {
      number: '02',
      icon: BadgeCheck,
      title: 'Get Verified',
      description: 'Complete our verification process to build trust with donors. Verified campaigns receive more donations.',
      features: [
        'Identity verification (KYC)',
        'Document verification',
        'Verified badge on campaign',
        'Higher donor confidence',
      ],
      image: 'https://images.unsplash.com/photo-1585909694668-0a6e0ddbfe8b?q=80&w=1000',
    },
    {
      number: '03',
      icon: Share2,
      title: 'Share Your Story',
      description: 'Spread the word through social media, email, and your personal network to reach potential donors.',
      features: [
        'One-click social sharing',
        'Email invite templates',
        'Shareable campaign links',
        'Track sharing analytics',
      ],
      image: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?q=80&w=1000',
    },
    {
      number: '04',
      icon: Heart,
      title: 'Receive Donations',
      description: 'Accept donations from anywhere in the world with secure payment processing and multiple currency support.',
      features: [
        'Credit/debit card payments',
        'Digital wallets support',
        'Multi-currency donations',
        'Recurring donation options',
      ],
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?q=80&w=1000',
    },
    {
      number: '05',
      icon: Wallet,
      title: 'Withdraw Funds',
      description: 'Transfer funds directly to your bank account with our fast and secure payout system.',
      features: [
        'Fast bank transfers',
        'Milestone-based releases',
        'Zero platform fees',
        'Transparent tracking',
      ],
      image: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?q=80&w=1000',
    },
  ];

  const trustFeatures = [
    {
      icon: Shield,
      title: 'Campaign Verification',
      description: 'Campaigns undergo review to help ensure compliance with our community guidelines.',
    },
    {
      icon: Eye,
      title: 'Progress Tracking',
      description: 'Campaign updates help keep donors informed about how funds are being used.',
    },
    {
      icon: Lock,
      title: 'Secure Payments',
      description: '256-bit SSL encryption protects all transactions. PCI DSS compliant payment processing.',
    },
    {
      icon: BadgeCheck,
      title: 'KYC Verification',
      description: 'Campaign creators undergo identity verification before they can withdraw funds.',
    },
  ];

  const faqs = [
    {
      question: 'How much does it cost to start a campaign?',
      answer: 'Creating a campaign on FundTheHope is completely free. FundTheHope charges 0% platform fees. Standard payment processing fees charged by payment providers (e.g., 2.9% + $0.30) apply.',
    },
    {
      question: 'How do I receive the funds raised?',
      answer: 'You can withdraw funds directly to your linked bank account at any time after completing verification. Standard payouts arrive within 2-5 business days.',
    },
    {
      question: 'What verification is required?',
      answer: 'We require identity verification (KYC) for all campaign creators before they can withdraw funds. This includes providing a government-issued ID and proof of address. Organizations may need additional documentation.',
    },
    {
      question: 'What if my campaign doesn\'t reach its goal?',
      answer: 'Unlike some platforms, we use flexible funding. You keep all the money you raise, even if you don\'t reach your goal. There\'s no all-or-nothing pressure.',
    },
    {
      question: 'What is FundTheHope\'s role?',
      answer: 'FundTheHope is a technology platform connecting campaign organizers with donors. We do not endorse, guarantee, or verify the accuracy or outcomes of campaigns. Campaign organizers are responsible for their campaigns and how they use funds.',
    },
    {
      question: 'Can I create a campaign for someone else?',
      answer: 'Yes! You can create campaigns on behalf of others. Simply select "Someone Else" during campaign creation and provide the beneficiary\'s information. They may need to complete verification for fund withdrawals.',
    },
    {
      question: 'What countries do you support?',
      answer: 'FundTheHope supports campaigns and donations from over 120 countries. We support multiple currencies and comply with regional regulations including GDPR, AML, and local payment laws.',
    },
    {
      question: 'What is the refund policy?',
      answer: 'Donations are generally non-refundable once processed. Refunds may be considered on a case-by-case basis at FundTheHope\'s sole discretion in cases of confirmed fraud or material policy violations. We do not guarantee refunds.',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-background to-primary/5 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                Start fundraising in minutes
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Our platform makes it easy to raise money for the causes you care about, 
                with built-in trust features that give donors confidence.
              </p>
              <div className="flex justify-center">
                <Link to="/create">
                  <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 px-8 py-6 text-lg rounded-xl">
                    Start a Campaign <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Steps Section - Dark Theme */}
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                How it works
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                From idea to funded campaign in 5 simple steps
              </p>
            </div>

            <div className="space-y-16">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isEven = index % 2 === 1;
                return (
                  <div
                    key={index}
                    className={`flex flex-col ${isEven ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12`}
                  >
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <span className="text-5xl font-bold text-emerald-900">{step.number}</span>
                        <div className="w-14 h-14 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-2xl flex items-center justify-center border border-emerald-500/30">
                          <Icon className="w-7 h-7 text-emerald-400" />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">{step.title}</h3>
                      <p className="text-lg text-gray-400 mb-6">{step.description}</p>
                      <ul className="space-y-3">
                        {step.features.map((feature, i) => (
                          <li key={i} className="flex items-center text-gray-300">
                            <CheckCircle2 className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Visual - Real Image */}
                    <div className="flex-1">
                      <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                        <img 
                          src={step.image} 
                          alt={step.title}
                          className="w-full h-full object-cover aspect-square"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Trust Features */}
        <section className="py-20 bg-background text-foreground">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-emerald-500/20 rounded-full mb-4">
                <Shield className="w-5 h-5 text-emerald-400 mr-2" />
                <span className="text-emerald-400 font-medium">Trust & Transparency</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Built-in trust features
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Industry-leading security and compliance measures protect every donation
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {trustFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors"
                  >
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Success Stories Section */}
        <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-amber-500/20 rounded-full mb-4">
                <Star className="w-5 h-5 text-amber-400 mr-2" />
                <span className="text-amber-400 font-medium">Success Stories</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Real Stories, Real Impact
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Hear from people whose lives were changed through the generosity of our community
              </p>
            </div>

            {storiesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
              </div>
            ) : successStories.length === 0 ? (
              <div className="text-center py-12">
                <Quote className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Success stories coming soon!</p>
                <p className="text-sm text-gray-500 mt-2">
                  Campaign creators can share their stories from their dashboard
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {successStories.map((story) => (
                  <div
                    key={story.id}
                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <img
                        src={story.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(story.user_name)}&background=random`}
                        alt={story.user_name}
                        className="w-12 h-12 rounded-full border-2 border-amber-500/50"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">{story.user_name}</h4>
                        <p className="text-sm text-gray-400">Raised ${story.amount_raised?.toLocaleString()}</p>
                      </div>
                      <Quote className="w-8 h-8 text-amber-500/30" />
                    </div>
                    
                    <h3 className="text-lg font-semibold text-amber-400 mb-2">{story.title}</h3>
                    <p className="text-gray-300 text-sm mb-4 line-clamp-4">{story.story}</p>
                    
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-gray-400">
                        <span className="text-emerald-400 font-medium">Outcome:</span> {story.outcome}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="text-center mt-8">
              <Link to="/share-story">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                  <Star className="w-4 h-4 mr-2" /> Share Your Story
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQs - Dark Theme */}
        <section className="py-20 bg-card">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Frequently asked questions
              </h2>
              <p className="text-xl text-gray-400">
                Everything you need to know about fundraising on FundTheHope
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-background rounded-xl border border-white/10 px-6"
                >
                  <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA - Dark Theme */}
        <section className="py-20 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-500/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-emerald-400 mr-2" />
              <span className="text-emerald-400 font-medium">Ready to get started?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Start your fundraising journey today
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Join people who have raised funds for causes they believe in.
            </p>
            <Link to="/create">
              <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 px-12 py-6 text-lg rounded-xl shadow-lg">
                <Heart className="mr-2 w-5 h-5" /> Start a Campaign
              </Button>
            </Link>
            <p className="text-sm text-gray-500 mt-4">
              No setup fees • Transparent pricing • Responsive support
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorksPage;
